import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Bell, 
  Plus, 
  Send,
  Users,
  AlertCircle,
  Info,
  CheckCircle,
  AlertTriangle,
  Trash2,
  Activity
} from "lucide-react";
import { useMasterNotifications } from '@/hooks/notifications/useMasterNotifications';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';

const AdminNotifications: React.FC = () => {
  const { notifications, isLoading, createNotification, deleteNotification, connectionStatus } = useMasterNotifications();
  const { toast } = useToast();
  
  const [newNotification, setNewNotification] = useState({
    title: '',
    message: '',
    priority: 'medium' as 'low' | 'medium' | 'high' | 'urgent',
    userId: '',
    type: 'system_error'
  });

  const [isCreating, setIsCreating] = useState(false);

  const handleCreateNotification = async () => {
    if (!newNotification.title || !newNotification.message) {
      toast({
        title: "Validation Error",
        description: "Please fill in both title and message fields.",
        variant: "destructive"
      });
      return;
    }

    setIsCreating(true);
    try {
      await createNotification(
        newNotification.title,
        newNotification.message,
        newNotification.type,
        newNotification.priority,
        newNotification.userId || undefined
      );
      
      // Reset form
      setNewNotification({
        title: '',
        message: '',
        priority: 'medium',
        userId: '',
        type: 'system_error'
      });

      toast({
        title: "Success",
        description: "Notification created successfully!"
      });
    } catch (error) {
      console.error('Error creating notification:', error);
      toast({
        title: "Error",
        description: "Failed to create notification. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsCreating(false);
    }
  };

  const getPriorityIcon = (priority: number) => {
    switch (priority) {
      case 4: return <AlertCircle className="h-4 w-4 text-destructive" />;
      case 3: return <AlertTriangle className="h-4 w-4 text-orange-500" />;
      case 2: return <Info className="h-4 w-4 text-blue-500" />;
      case 1: return <CheckCircle className="h-4 w-4 text-green-500" />;
      default: return <Bell className="h-4 w-4" />;
    }
  };

  const getPriorityLabel = (priority: number) => {
    switch (priority) {
      case 4: return 'Urgent';
      case 3: return 'High';
      case 2: return 'Medium';
      case 1: return 'Low';
      default: return 'Medium';
    }
  };

  const getPriorityVariant = (priority: number) => {
    switch (priority) {
      case 4: return 'destructive' as const;
      case 3: return 'secondary' as const;
      case 2: return 'outline' as const;
      case 1: return 'secondary' as const;
      default: return 'outline' as const;
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <Bell className="h-6 w-6" />
              <h1 className="text-3xl font-bold">Notification Management</h1>
              <Badge variant="secondary">Admin</Badge>
            </div>
            <p className="text-muted-foreground">
              Create and manage system notifications
            </p>
          </div>
          
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 text-sm">
              <Activity className="h-4 w-4" />
              <span className={connectionStatus.isConnected ? 'text-green-600' : 'text-red-600'}>
                {connectionStatus.isConnected ? 'Connected' : 'Disconnected'}
              </span>
            </div>
          </div>
        </div>

        <Tabs defaultValue="create" className="space-y-6">
          <TabsList>
            <TabsTrigger value="create" className="flex items-center gap-2">
              <Plus className="h-4 w-4" />
              Create Notification
            </TabsTrigger>
            <TabsTrigger value="manage" className="flex items-center gap-2">
              <Bell className="h-4 w-4" />
              Manage Notifications ({notifications.length})
            </TabsTrigger>
          </TabsList>

          {/* Create Notification Tab */}
          <TabsContent value="create">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Plus className="h-5 w-5" />
                  Create New Notification
                </CardTitle>
                <CardDescription>
                  Send notifications to specific users or broadcast to all users
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Title</label>
                    <Input
                      placeholder="Enter notification title"
                      value={newNotification.title}
                      onChange={(e) => setNewNotification(prev => ({ ...prev, title: e.target.value }))}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Priority</label>
                    <Select 
                      value={newNotification.priority} 
                      onValueChange={(value) => setNewNotification(prev => ({ ...prev, priority: value as any }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="urgent">Urgent</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">User ID (Optional)</label>
                    <Input
                      placeholder="Leave empty for broadcast to all users"
                      value={newNotification.userId}
                      onChange={(e) => setNewNotification(prev => ({ ...prev, userId: e.target.value }))}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Type</label>
                    <Select 
                      value={newNotification.type} 
                      onValueChange={(value) => setNewNotification(prev => ({ ...prev, type: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="system_error">System Error</SelectItem>
                        <SelectItem value="user_welcome">User Welcome</SelectItem>
                        <SelectItem value="security_alert">Security Alert</SelectItem>
                        <SelectItem value="order_created">Order Created</SelectItem>
                        <SelectItem value="payment_success">Payment Success</SelectItem>
                        <SelectItem value="marketing_promotion">Marketing Promotion</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Message</label>
                  <Textarea
                    placeholder="Enter notification message"
                    value={newNotification.message}
                    onChange={(e) => setNewNotification(prev => ({ ...prev, message: e.target.value }))}
                    rows={4}
                  />
                </div>

                <Separator />

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Users className="h-4 w-4" />
                    {newNotification.userId ? 'Sending to specific user' : 'Broadcasting to all users'}
                  </div>
                  
                  <Button 
                    onClick={handleCreateNotification}
                    disabled={isCreating || !newNotification.title || !newNotification.message}
                    className="flex items-center gap-2"
                  >
                    <Send className="h-4 w-4" />
                    {isCreating ? 'Creating...' : 'Create Notification'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Manage Notifications Tab */}
          <TabsContent value="manage">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bell className="h-5 w-5" />
                  All Notifications
                </CardTitle>
                <CardDescription>
                  View and manage all system notifications
                </CardDescription>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                      <p className="text-muted-foreground">Loading notifications...</p>
                    </div>
                  </div>
                ) : notifications.length === 0 ? (
                  <div className="text-center py-12">
                    <Bell className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-semibold mb-2">No notifications</h3>
                    <p className="text-muted-foreground">No notifications have been created yet.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {notifications.map((notification) => (
                      <Card key={notification.id} className="relative">
                        <CardHeader className="pb-3">
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-3 flex-1">
                              {getPriorityIcon(notification.priority)}
                              <div className="flex-1 space-y-1">
                                <div className="flex items-center gap-2">
                                  <CardTitle className="text-base">{notification.title}</CardTitle>
                                  <Badge variant={getPriorityVariant(notification.priority)} className="text-xs">
                                    {getPriorityLabel(notification.priority)}
                                  </Badge>
                                  <Badge variant="outline" className="text-xs">
                                    {notification.type}
                                  </Badge>
                                </div>
                                <CardDescription className="text-sm">
                                  {format(new Date(notification.timestamp), 'PPpp')}
                                </CardDescription>
                              </div>
                            </div>
                            
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => deleteNotification(notification.id)}
                              className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                              <span className="sr-only">Delete notification</span>
                            </Button>
                          </div>
                        </CardHeader>
                        
                        <CardContent className="pt-0">
                          <p className="text-sm text-muted-foreground leading-relaxed">
                            {notification.message}
                          </p>
                          
                          {notification.metadata && Object.keys(notification.metadata).length > 0 && (
                            <>
                              <Separator className="my-3" />
                              <div className="text-xs text-muted-foreground">
                                <strong>Metadata:</strong> {JSON.stringify(notification.metadata, null, 2)}
                              </div>
                            </>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminNotifications;